import React, { useEffect, useRef, useState } from "react";
import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import Flex from "../../../../../../components/Flex";
import { Form, Input, Typography, Checkbox, Radio } from "antd";
import FormGroupItem from "../FormGroupItem";
import { CoverWizardForm, FormGroupTouched } from "../../CoverWizardModal";

type ImportantConsiderationsPageProps = {
  onNextButtonClick?: () => void;
  onBackButtonClick?: () => void;
  //the current form values are needed in order to have field values at mount (useWatch outputs current values at second rerender)
  initialFormValues: CoverWizardForm;
  formGroupTouchedState: [FormGroupTouched, React.Dispatch<FormGroupTouched>];
};

const yesOrNoRadios = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const hasNonRemovableRailsFieldName = "hasNonRemovableRails";
const nonRemovableRailsLocationFieldName = "nonRemovableRailsLocation";

const hasSpoutOnDeckFieldName = "hasSpoutOnDeck";
const spoutLocationFieldName = "spoutLocation";

const fillSpoutHeightFieldName = "fillSpoutHeight";

const fillSpoutBelowCopingFieldName = "fillSpoutBelowCoping";
const fillSpoutBelowCopingLocationFieldName = "fillSpoutBelowCopingLocation";

export default function ImportantConsiderationsPage({
  onNextButtonClick,
  onBackButtonClick,
  initialFormValues,
  formGroupTouchedState: [formGroupTouched, setFormGroupTouched],
}: ImportantConsiderationsPageProps) {
  const form = Form.useFormInstance<CoverWizardForm>();

  //---------non-removable rails group state
  const hasNonRemovableRailsAnswer =
    Form.useWatch(hasNonRemovableRailsFieldName, { form, preserve: true }) ??
    initialFormValues.hasNonRemovableRails;
  const nonRemovableRailsLocation =
    Form.useWatch(nonRemovableRailsLocationFieldName, {
      form,
      preserve: true,
    }) ?? initialFormValues.nonRemovableRailsLocation;
  const nonRemovableRailsExist = hasNonRemovableRailsAnswer === "yes";

  //---------spout deck group state
  const hasSpoutOnDeckAnswer =
    Form.useWatch(hasSpoutOnDeckFieldName, { form, preserve: true }) ??
    initialFormValues.hasSpoutOnDeck;
  const spoutLocation =
    Form.useWatch(spoutLocationFieldName, { form, preserve: true }) ??
    initialFormValues.spoutLocation;
  const hasSpoutOnDeck = hasSpoutOnDeckAnswer === "yes";

  //---------fill spout height group state
  const fillSpoutHeight =
    Form.useWatch(fillSpoutHeightFieldName, { preserve: true, form }) ??
    initialFormValues.fillSpoutHeight;

  //---------spout below coping group state
  const fillSpoutBelowCopingAnswer =
    Form.useWatch(fillSpoutBelowCopingFieldName, { preserve: true, form }) ??
    initialFormValues.fillSpoutBelowCoping;
  const fillSpoutBelowCopingLocation =
    Form.useWatch(fillSpoutBelowCopingLocationFieldName, {
      form,
      preserve: true,
    }) ?? initialFormValues.fillSpoutBelowCopingLocation;
  const fillSpoutBelowCoping = fillSpoutBelowCopingAnswer === "yes";

  return (
    <WizardPageWrapper
      title="Important Considerations"
      summary={<GeneralPageSummary />}
      onBackButtonClick={onBackButtonClick}
      onNextButtonClick={onNextButtonClick}
    >
      <Flex vertical gap="16px">
        <FormGroupItem
          label="Non-Removable Rails"
          closeButtonText={
            formGroupTouched.nonRemovableRailsGroup ? "Save" : "Next"
          }
          onGroupClosed={() =>
            setFormGroupTouched({
              ...formGroupTouched,
              nonRemovableRailsGroup: true,
            })
          }
          notExpandedDisplayValue={
            nonRemovableRailsExist && !!nonRemovableRailsLocation
              ? `Location(s) ${nonRemovableRailsLocation}`
              : nonRemovableRailsExist
              ? "Yes"
              : "None"
          }
          expandedOnMount={!formGroupTouched.nonRemovableRailsGroup}
        >
          <Flex vertical gap="0.4rem">
            <Flex vertical gap="0.5rem">
              <Typography.Text>Are there non-removable rails?</Typography.Text>
              <Radio.Group
                options={yesOrNoRadios}
                value={hasNonRemovableRailsAnswer}
                onChange={(event) => {
                  const newValue = event.target.value;
                  if (newValue === "no") {
                    return form.setFieldsValue({
                      [hasNonRemovableRailsFieldName]: newValue,
                      [nonRemovableRailsLocationFieldName]: "",
                    });
                  }
                  form.setFieldValue(hasNonRemovableRailsFieldName, newValue);
                }}
              />
            </Flex>
            {nonRemovableRailsExist && (
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name={nonRemovableRailsLocationFieldName}
                label="Where?"
              >
                <Input />
              </Form.Item>
            )}
          </Flex>
        </FormGroupItem>
        {formGroupTouched.nonRemovableRailsGroup && (
          <FormGroupItem
            label="Spout"
            closeButtonText={
              formGroupTouched.spoutGroupTouched ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                spoutGroupTouched: true,
              })
            }
            notExpandedDisplayValue={
              hasSpoutOnDeck && !!spoutLocation
                ? `Location ${spoutLocation}`
                : hasSpoutOnDeck
                ? "Yes"
                : "None"
            }
            expandedOnMount={!formGroupTouched.spoutGroupTouched}
          >
            <Flex vertical gap="0.4rem">
              <Flex vertical gap="0.5rem">
                <Typography.Text>
                  Does the pool have a spout set back on deck?
                </Typography.Text>
                <Radio.Group
                  value={hasSpoutOnDeckAnswer}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    if (newValue === "no") {
                      return form.setFieldsValue({
                        [hasSpoutOnDeckFieldName]: newValue,
                        [spoutLocationFieldName]: "",
                        [fillSpoutHeightFieldName]: "",
                        [fillSpoutBelowCopingFieldName]: "no",
                        [fillSpoutBelowCopingLocationFieldName]: "",
                      });
                    }
                    form.setFieldValue(hasSpoutOnDeckFieldName, newValue);
                  }}
                  options={yesOrNoRadios}
                />
              </Flex>
              {hasSpoutOnDeck && (
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={spoutLocationFieldName}
                  label="Where?"
                >
                  <Input />
                </Form.Item>
              )}
            </Flex>
          </FormGroupItem>
        )}
        {formGroupTouched.spoutGroupTouched && (
          <FormGroupItem
            label="Fill Spout Height"
            closeButtonText={
              formGroupTouched.fillSpoutHeightGroup ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                fillSpoutHeightGroup: true,
              })
            }
            notExpandedDisplayValue={
              !!fillSpoutHeight ? fillSpoutHeight : "No fill height"
            }
            expandedOnMount={!formGroupTouched.fillSpoutHeightGroup}
          >
            <Flex vertical gap="0.4rem">
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name={fillSpoutHeightFieldName}
                label="How high is the fill spout raised above the deck?"
              >
                <Input />
              </Form.Item>
            </Flex>
          </FormGroupItem>
        )}
        {formGroupTouched.fillSpoutHeightGroup && (
          <FormGroupItem
            label="Spout Below Coping"
            closeButtonText={"Save"}
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                spoutBelowCopingGroup: true,
              })
            }
            notExpandedDisplayValue={
              fillSpoutBelowCoping && fillSpoutBelowCopingLocation
                ? fillSpoutBelowCopingLocation
                : fillSpoutBelowCoping
                ? "Yes"
                : "No"
            }
            expandedOnMount={!formGroupTouched.spoutBelowCopingGroup}
          >
            <Flex vertical gap="0.4rem">
              <Flex vertical gap="0.5rem">
                <Typography.Text>
                  Does the fill spout dip below the coping?
                </Typography.Text>
                <Radio.Group
                  options={yesOrNoRadios}
                  value={fillSpoutBelowCopingAnswer}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    if (newValue === "no") {
                      return form.setFieldsValue({
                        [fillSpoutBelowCopingFieldName]: newValue,
                        [fillSpoutBelowCopingLocationFieldName]: "",
                      });
                    }
                    form.setFieldValue(fillSpoutBelowCopingFieldName, newValue);
                  }}
                />
              </Flex>
              {fillSpoutBelowCoping && (
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={fillSpoutBelowCopingLocationFieldName}
                  label="How far below the coping?"
                >
                  <Input />
                </Form.Item>
              )}
            </Flex>
          </FormGroupItem>
        )}
      </Flex>
    </WizardPageWrapper>
  );
}
